import { useCallback, useEffect, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import useDebounceState from '@/hooks/useDebounceState';
import usePagination from '@/hooks/usePagination';
import {
  getSkillFilterOption,
  SkillMenuItem,
} from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/CreateFeedbackModal/components/RelatedSkillsOrMindsets/utils';
import { SkillDetailsFragment } from '@/modules/skill-matrix/graphql/fragment/generated/SkillDetails';
import { useAllSkillsListQuery } from '@/modules/skill-matrix/graphql/queries/generated/allSkills';
import { PeerFeedbackMindsetRatingInputType, SkillTypeEnum } from '@/types/schema';

import { useUserPraiseMindsetsQuery } from '../../graphql/queries/generated/usersPraiseMindsets';

const usePraiseRelatedMindsets = ({
  userIds,
  mindsetRatings,
  onChange,
}: {
  userIds?: string[];
  mindsetRatings: PeerFeedbackMindsetRatingInputType[];
  onChange: (value: PeerFeedbackMindsetRatingInputType[]) => void;
}) => {
  const [open, { toggle }] = useToggle(false);
  const [searchText, setSearchText, debouncedSearchText] = useDebounceState('');
  const [selectedMindsetsState, setSelectedMindsets] = useState<SkillMenuItem[]>();
  const selectedMindsetIds = mindsetRatings.map((rating) => rating.mindsetId);

  const [{ data: mindsetData, fetching }] = useAllSkillsListQuery({
    variables: {
      filters: {
        ids: selectedMindsetIds,
        types: [SkillTypeEnum.Behavior],
      },
      first: -1,
    },
  });
  const selectedMindsets = useMemo(
    () => mindsetData?.skills.edges?.map(({ node }) => node),
    [mindsetData?.skills.edges],
  );

  const { data } = usePagination(
    useUserPraiseMindsetsQuery,
    {
      filters: {
        ids: userIds,
      },
      first: -1,
    },
    { edgeKey: 'users', pause: !userIds?.length },
  );

  useEffect(() => {
    if (selectedMindsetIds.length && !selectedMindsetsState && !fetching) {
      const selectedMindsetsMap = selectedMindsets?.map((mindset) => getSkillFilterOption(mindset));
      setSelectedMindsets(selectedMindsetsMap);
    }
  }, [fetching, selectedMindsetIds.length, selectedMindsets, selectedMindsetsState]);

  const filteredMindsets = useMemo(() => {
    const allMindsets = data.reduce((acc, node) => {
      if (node.orgRole?.mindsets?.length) {
        acc.push(...node.orgRole!.mindsets!);
      }
      return acc;
    }, [] as SkillDetailsFragment[]);
    const mindsets = uniqBy(allMindsets, 'id');

    return debouncedSearchText
      ? mindsets.filter((mindset) =>
          mindset.name.toLowerCase().includes(debouncedSearchText.toLowerCase()),
        )
      : mindsets;
  }, [data, debouncedSearchText]);

  const onRatingChange = useCallback(
    (id: string, rating: number) => {
      const newMindsetRating = [...mindsetRatings];
      const index = newMindsetRating.findIndex((rating) => rating.mindsetId === id);
      if (index !== -1) {
        newMindsetRating[index].rating = rating;
      }
      onChange(newMindsetRating);
    },
    [mindsetRatings, onChange],
  );

  const mindsetRatingMap = useMemo(() => {
    const newMindsetRatingMap = {} as Record<string, number>;
    mindsetRatings.forEach((selectedValue) => {
      if (selectedValue.rating) {
        newMindsetRatingMap[selectedValue.mindsetId] = selectedValue.rating;
      }
    });
    return newMindsetRatingMap;
  }, [mindsetRatings]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    return filteredMindsets?.length && userIds?.length
      ? [
          {
            id: 'mindsets',
            options: filteredMindsets.map((mindset) => getSkillFilterOption(mindset)),
          },
        ]
      : [];
  }, [filteredMindsets, userIds?.length]);

  const onAddMindset = useEvent((filterOptions: FilterOption[]) => {
    setSelectedMindsets(filterOptions as SkillMenuItem[]);
    const keyBySkillRatings = keyBy(mindsetRatings, 'mindsetId');
    const newSkillRatings = (filterOptions as SkillMenuItem[]).map((skillOption) => ({
      mindsetId: skillOption.id,
      rating: keyBySkillRatings[skillOption.id]?.rating,
    }));
    onChange(newSkillRatings);
  });

  const onRemoveMindset = useCallback(
    (id: string) => {
      const updatedMindsets = mindsetRatings.filter((mindset) => mindset.mindsetId !== id);
      const updatedSelectedMindsetState = selectedMindsetsState?.filter(
        (mindsetMenuItem: SkillMenuItem) => mindsetMenuItem.id !== id,
      );
      setSelectedMindsets(updatedSelectedMindsetState);
      onChange(updatedMindsets);
    },
    [mindsetRatings, onChange, selectedMindsetsState],
  );

  return {
    selectedMindsets: selectedMindsetIds.length ? selectedMindsets : [],
    fetching,
    mindsetRatingMap,
    onRatingChange,
    searchText,
    setSearchText,
    filterGroups,
    open,
    toggle,
    onAddMindset,
    selectedMindsetsState,
    onRemoveMindset,
  };
};

export default usePraiseRelatedMindsets;
